import React from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { fixMojibake } from '../../../utils/textHelpers';

// Format time string from "HH:MM:SS" to "HH:MM"
const formatTime = (timeStr: string | null | undefined): string => {
    if (!timeStr) return '';
    // Handle "HH:MM:SS" or "HH:MM" format
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
};

interface KioskAbsenceListProps {
    absenteLehrkraefte: any[];
    absenteKlassen: any[];
    absentTagByKurzel: Map<string, string>;
    displayKurzel: (v: string) => string;
}

export const KioskAbsenceList: React.FC<KioskAbsenceListProps> = ({
    absenteLehrkraefte,
    absenteKlassen,
    absentTagByKurzel,
    displayKurzel
}) => {
    // Sort absent teachers by role priority (ADM → ERZ → LK → EXT), then alphabetically within each role
    const sortedLehrkraefte = React.useMemo(() => {
        const getRolePriority = (role: string): number => {
            if (role === 'Admin' || role === 'Super Admin') return 0; // ADM first
            if (role === 'Erzieher*innen') return 1; // ERZ second
            if (role === 'Teacher') return 2; // LK third
            if (role === 'Externe') return 3; // EXT fourth
            return 4; // Unknown roles last
        };

        return [...absenteLehrkraefte].sort((a, b) => {
            // First compare by role priority
            const priorityA = getRolePriority(a.role);
            const priorityB = getRolePriority(b.role);
            if (priorityA !== priorityB) {
                return priorityA - priorityB;
            }
            // Then alphabetically by name within the same role
            const nameA = (a.name || '').toLowerCase();
            const nameB = (b.name || '').toLowerCase();
            return nameA.localeCompare(nameB, 'de');
        });
    }, [absenteLehrkraefte]);

    return (
        <Card className="bg-white border-2 border-slate-200 shadow-sm flex-1 overflow-hidden flex flex-col">
            <CardContent className="p-2 space-y-2 overflow-y-auto no-scrollbar">
                <div>
                    <div className="font-black text-slate-900 kiosk-text-xs uppercase tracking-wider mb-1 border-b border-slate-100 pb-0.5">Abwesende Fachkräfte</div>
                    <div className="flex flex-wrap gap-1.5">
                        {sortedLehrkraefte.length > 0 ? (
                            sortedLehrkraefte.map((l, idx) => {
                                let suffix = '';
                                let roleClass = 'text-slate-500 bg-slate-100';

                                if (l.role === 'Teacher') {
                                    suffix = 'LK';
                                    roleClass = 'text-blue-700 bg-blue-100 border-blue-200';
                                }
                                else if (l.role === 'Erzieher*innen') {
                                    suffix = 'ERZ';
                                    roleClass = 'text-green-700 bg-green-100 border-green-200';
                                }
                                else if (l.role === 'Externe') {
                                    suffix = 'EXT';
                                    roleClass = 'text-slate-600 bg-slate-100 border-slate-200';
                                }
                                else if (l.role === 'Admin' || l.role === 'Super Admin') {
                                    suffix = 'ADM';
                                    roleClass = 'text-purple-700 bg-purple-100 border-purple-200';
                                }

                                const showStatus = l.status && l.status !== 'abwesend';
                                const hasTimeRange = l.startTime && l.endTime;
                                const timeDisplay = hasTimeRange 
                                    ? `${formatTime(l.startTime)}-${formatTime(l.endTime)}`
                                    : null;

                                return (
                                    <span key={idx} className="bg-red-50 border border-red-200 rounded px-1.5 py-0.5 flex items-center gap-1">
                                        <span className="kiosk-text-xs font-bold text-red-900">{l.name}</span>
                                        {suffix && (
                                            <span className={`kiosk-text-2xs font-medium px-1 rounded border-b ${roleClass}`}>
                                                {suffix}
                                            </span>
                                        )}
                                        {timeDisplay && (
                                            <span className="kiosk-text-2xs font-medium text-red-600">
                                                ({timeDisplay})
                                            </span>
                                        )}
                                        {showStatus ? <span className="kiosk-text-2xs font-medium text-red-700">({l.status})</span> : ''}
                                    </span>
                                );
                            })
                        ) : (
                            <span className="kiosk-text-xs text-slate-500 italic">Alle anwesend</span>
                        )}
                    </div>
                </div>

                <div>
                    <div className="font-black text-slate-900 kiosk-text-xs uppercase tracking-wider mb-1.5 border-b border-slate-100 pb-1">Abwesende Klassen</div>
                    <div className="flex flex-wrap gap-1">
                        {absenteKlassen.length > 0 ? (
                            absenteKlassen.map((k: any, idx: number) => (
                                <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-900 border border-slate-300 kiosk-text-xs font-bold px-1 py-0.5">
                                    {k.klasse}{k.stundenangabe ? ` (${fixMojibake(k.stundenangabe)})` : ''}
                                </Badge>
                            ))
                        ) : (
                            <span className="kiosk-text-xs text-slate-500 italic">Alle anwesend</span>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
